import { forwardRef } from 'react';

export type HeaderVariant = 'primary' | 'secondary' | 'minimal';
export type HeaderSize = 'sm' | 'md' | 'lg';

export interface HeaderContainerProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: HeaderVariant;
  size?: HeaderSize;
  children: React.ReactNode;
  hasBorder?: boolean;
  hasShadow?: boolean;
}

const HeaderContainer = forwardRef<HTMLDivElement, HeaderContainerProps>(({
  variant = 'primary',
  size = 'md',
  children,
  hasBorder = true,
  hasShadow = false,
  className = '',
  ...props
}, ref) => {
  const getContainerClasses = () => {
    const baseClasses = [
      'flex',
      'items-center',
      'justify-between',
      'w-full',
      'transition-all',
      'duration-200',
      'box-border' // Ensure consistent box-sizing
    ];

    // Size classes (consistent header height - 64px exactly)
    const sizeClasses = {
      sm: ['h-12', 'px-3'],
      md: ['h-16', 'px-4'], // 64px exactly
      lg: ['h-20', 'px-6']
    };

    // Variant classes
    const variantClasses = {
      primary: ['bg-bg-secondary'],
      secondary: ['bg-bg-primary'],
      minimal: ['bg-transparent']
    };

    // Border classes - using box-sizing: border-box to include border in height calculation
    const borderClasses = hasBorder ? ['border-b', 'border-border-light'] : [];

    // Shadow classes
    const shadowClasses = hasShadow ? ['shadow-sm'] : [];

    return [
      ...baseClasses,
      ...sizeClasses[size],
      ...variantClasses[variant],
      ...borderClasses,
      ...shadowClasses,
      className
    ].filter(Boolean).join(' ');
  };

  return (
    <div
      ref={ref}
      className={getContainerClasses()}
      {...props}
    >
      {children}
    </div>
  );
});

HeaderContainer.displayName = 'HeaderContainer';

export default HeaderContainer;